import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@tanstack/react-query";
import { invoicesService } from "@/api/invoices";
import { paymentService } from "@/api/payment";
import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";

const PaymentPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const companyId = user?.id;

    const [paymentMethod, setPaymentMethod] = useState("clicktopay");
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [savedMethods, setSavedMethods] = useState([]);
    const [useSavedMethod, setUseSavedMethod] = useState(false);
    const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
    const [clickToPayLoaded, setClickToPayLoaded] = useState(false);

    // Load Click to Pay SDK
    useEffect(() => {
        const loadClickToPaySDK = () => {
            const scriptId = 'clicktopay-sdk';

            if (document.getElementById(scriptId)) {
                setClickToPayLoaded(true);
                return;
            }

            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://secure.clicktopay.com/sdk/v2/clicktopay.js'; // Replace with actual SDK URL
            script.async = true;
            script.onload = () => {
                setClickToPayLoaded(true);
            };
            script.onerror = () => {
                console.error('Failed to load Click to Pay SDK');
                setPaymentError("Impossible de charger le service de paiement");
            };
            document.head.appendChild(script);
        };

        loadClickToPaySDK();
    }, []);

    // Fetch invoice
    const { data: invoice, isLoading, error } = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => invoicesService.getInvoice(id),
        retry: false,
    });

    // Fetch saved payment methods
    const { data: savedMethodsData } = useQuery({
        queryKey: ["saved-payment-methods", companyId],
        queryFn: () => paymentService.getSavedPaymentMethods(companyId),
        enabled: !!companyId && paymentMethod === "clicktopay",
    });

    useEffect(() => {
        if (savedMethodsData) {
            setSavedMethods(savedMethodsData);
            if (savedMethodsData.length > 0) {
                setSelectedSavedMethod(savedMethodsData[0].id);
            }
        }
    }, [savedMethodsData]);

    // Check if invoice belongs to company
    useEffect(() => {
        if (invoice && companyId) {
            if ((invoice.company?.id || invoice.user?.id) !== companyId) {
                navigate("/factures");
            }
            if (invoice.status === "PAID") {
                navigate(`/factures/${id}`);
            }
        }
    }, [invoice, companyId, navigate, id]);

    // Payment mutation
    const paymentMutation = useMutation({
        mutationFn: (paymentData) => invoicesService.markAsPaid(id, paymentData),
        onSuccess: () => {
            alert("Paiement effectué avec succès !");
            navigate(`/factures/${id}`);
        },
        onError: (error) => {
            setPaymentError(error.message || "Erreur lors du paiement");
            setIsProcessing(false);
        },
    });

    const handlePayment = async () => {
        if (!invoice) return;

        setIsProcessing(true);
        setPaymentError(null);

        try {
            let paymentResult;

            switch (paymentMethod) {
                case "clicktopay": {
                    if (!clickToPayLoaded) {
                        throw new Error("Service de paiement non disponible");
                    }

                    const paymentData = {
                        invoiceId: id,
                        amount: invoice.total,
                        currency: "TND",
                        companyId,
                        useSavedMethod,
                        savedMethodId: useSavedMethod ? selectedSavedMethod : null
                    };

                    paymentResult = await paymentService.processClickToPay(paymentData);
                    break;
                }

                case "card":
                    paymentResult = await paymentService.processCardPayment({
                        invoiceId: id,
                        amount: invoice.total,
                        companyId
                    });
                    break;

                case "transfer":
                    paymentResult = await paymentService.initiateBankTransfer({
                        invoiceId: id,
                        amount: invoice.total,
                        companyId
                    });
                    break;

                default:
                    throw new Error("Méthode de paiement non supportée");
            }

            if (paymentResult.success) {
                paymentMutation.mutate({
                    paymentMethod,
                    transactionId: paymentResult.transactionId,
                    paymentDetails: paymentResult.paymentDetails
                });
            } else if (paymentResult.cancelled) {
                setIsProcessing(false);
                return;
            } else {
                throw new Error(paymentResult.error || "Échec du paiement");
            }
        } catch (error) {
            setPaymentError(error.message || "Une erreur est survenue lors du paiement");
            setIsProcessing(false);
        }
    };


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    if (!invoice) {
        return (
            <div className="p-6">
                <TaleDashbaordMainCard className="p-6">
                    <div className="text-center py-8">
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                            Facture non trouvée
                        </h2>
                        <TaleButton onClick={() => navigate("/factures")}>
                            Retour aux factures
                        </TaleButton>
                    </div>
                </TaleDashbaordMainCard>
            </div>
        );
    }

    return (
        <div className="p-6">

                {/* Main Card Container */}
                <TaleDashbaordMainCard className="p-6">
                    <div className="mb-6">
                        <button
                            onClick={() => navigate(`/factures/${id}`)}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-2 mb-2"
                        >
                            <i className="fas fa-arrow-left"></i>
                            Retour à la facture
                        </button>
                        <h1 className="text-2xl font-bold">Paiement de la facture #{id}</h1>
                        <p className="text-gray-600">
                            Montant à régler: <span className="font-bold text-green-600">
                            {formatCurrency(invoice.total)}
                        </span>
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Payment Form - Main Section */}
                        <div className="lg:col-span-2">
                            <div className="space-y-6">
                                {/* Payment Method Selection */}
                                <div>
                                    <h2 className="text-lg font-semibold mb-6">Choisissez votre moyen de paiement</h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <PaymentMethodOption
                                            method="clicktopay"
                                            label="Click to Pay"
                                            description="Paiement instantané et sécurisé"
                                            icon="bolt"
                                            color="blue"
                                            isSelected={paymentMethod === "clicktopay"}
                                            onSelect={() => setPaymentMethod("clicktopay")}
                                        />


                                        <PaymentMethodOption
                                            method="transfer"
                                            label="Virement bancaire"
                                            description="Instructions de virement"
                                            icon="university"
                                            color="green"
                                            isSelected={paymentMethod === "transfer"}
                                            onSelect={() => setPaymentMethod("transfer")}
                                        />
                                    </div>

                                    {/* Click to Pay Specific Options */}
                                    {paymentMethod === "clicktopay" && savedMethods.length > 0 && (
                                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                            <div className="flex items-center mb-3">
                                                <input
                                                    type="checkbox"
                                                    id="useSavedMethod"
                                                    checked={useSavedMethod}
                                                    onChange={(e) => setUseSavedMethod(e.target.checked)}
                                                    className="mr-3"
                                                />
                                                <label htmlFor="useSavedMethod" className="font-medium">
                                                    Utiliser une méthode de paiement enregistrée
                                                </label>
                                            </div>

                                            {useSavedMethod && (
                                                <div className="mt-3">
                                                    <select
                                                        value={selectedSavedMethod || ""}
                                                        onChange={(e) => setSelectedSavedMethod(e.target.value)}
                                                        className="w-full px-4 py-2 border rounded-lg"
                                                    >
                                                        {savedMethods.map((method) => (
                                                            <option key={method.id} value={method.id}>
                                                                {method.cardType} •••• {method.lastFourDigits}
                                                                ({method.expiryMonth}/{method.expiryYear})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            <div className="mt-4 text-sm text-blue-700">
                                                <i className="fas fa-info-circle mr-2"></i>
                                                Click to Pay utilise les dernières normes de sécurité pour protéger vos données.
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Payment Instructions */}
                                {paymentMethod === "transfer" && (
                                    <div className="p-6 border border-gray-200 rounded-lg">
                                        <h3 className="text-lg font-semibold mb-4">Instructions de virement</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Banque:</span>
                                                <span className="font-medium">Tale Bank</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">IBAN:</span>
                                                <span className="font-medium">FR76 1234 5678 9012 3456 7890 123</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">BIC:</span>
                                                <span className="font-medium">ABCD1234567</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Bénéficiaire:</span>
                                                <span className="font-medium">Tale Platform SAS</span>
                                            </div>
                                            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                                                <p className="text-sm text-yellow-700">
                                                    <strong>Important:</strong> Indiquez le numéro de facture <strong>#{id}</strong>
                                                    dans la référence du virement pour un traitement rapide.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Payment Error Display */}
                                {paymentError && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-center">
                                            <i className="fas fa-exclamation-circle text-red-500 mr-3 text-xl"></i>
                                            <div>
                                                <div className="font-medium text-red-700">Erreur de paiement</div>
                                                <div className="text-red-600">{paymentError}</div>
                                                <button
                                                    onClick={() => setPaymentError(null)}
                                                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                                                >
                                                    <i className="fas fa-times mr-1"></i>
                                                    Fermer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Terms and Security */}
                                <div className="p-6 border border-gray-200 rounded-lg">
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <input
                                                type="checkbox"
                                                id="terms"
                                                className="mt-1 mr-3"
                                                required
                                            />
                                            <label htmlFor="terms" className="text-sm text-gray-600">
                                                <span className="font-medium">Acceptation des conditions</span><br/>
                                                J'accepte les conditions générales de vente et de paiement.
                                                En procédant au paiement, je confirme que les informations
                                                fournies sont exactes et que je suis autorisé à utiliser
                                                ce moyen de paiement.
                                            </label>
                                        </div>

                                    </div>
                                </div>

                                {/* Payment Action */}
                                <div className="flex justify-between items-center">
                                    <div className="text-sm text-gray-600">
                                        <i className="fas fa-lock mr-2"></i>
                                        Transaction 100% sécurisée
                                    </div>
                                    <TaleButton
                                        onClick={handlePayment}
                                        loading={isProcessing || paymentMutation.isPending}
                                        disabled={isProcessing || paymentMutation.isPending || !clickToPayLoaded}
                                        className="bg-green-600 hover:bg-green-700 border-green-600 px-8 py-3 text-lg min-w-[200px]"
                                    >
                                        {isProcessing ? (
                                            <span className="flex items-center">
                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                Traitement...
                                            </span>
                                        ) : paymentMutation.isPending ? (
                                            <span className="flex items-center">
                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                Confirmation...
                                            </span>
                                        ) : (
                                            <span className="flex items-center">
                                                <i className="fas fa-lock mr-2"></i>
                                                Payer {formatCurrency(invoice.total)}
                                            </span>
                                        )}
                                    </TaleButton>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Summary Sidebar */}
                        <div className="space-y-6">
                            {/* Invoice Summary */}
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4">Récapitulatif de la facture</h3>

                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Numéro</span>
                                        <span className="font-medium">#{invoice.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Date</span>
                                        <span className="font-medium">
                                            {new Date(invoice.creationDate).toLocaleDateString('fr-FR')}
                                        </span>
                                    </div>
                                    {invoice.dueDate && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Échéance</span>
                                            <span className="font-medium">
                                                {new Date(invoice.dueDate).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Client</span>
                                        <span className="font-medium text-right">
                                            {invoice.company?.companyName || invoice.user?.companyName}
                                        </span>
                                    </div>
                                </div>

                                <div className="border-t my-4 pt-4">
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span>Montant HT</span>
                                            <span>{formatCurrency(invoice.total - (invoice.tax || 0))}</span>
                                        </div>
                                        {invoice.tax > 0 && (
                                            <div className="flex justify-between">
                                                <span>TVA ({((invoice.tax / (invoice.total - invoice.tax)) * 100).toFixed(0)}%)</span>
                                                <span>{formatCurrency(invoice.tax)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-bold text-lg pt-3 border-t">
                                            <span>Total à payer</span>
                                            <span className="text-green-600">{formatCurrency(invoice.total)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Security Badge */}
                            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                                <div className="text-center">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-4">
                                        <i className="fas fa-shield-alt text-2xl text-blue-500"></i>
                                    </div>
                                    <h4 className="font-semibold text-blue-700 mb-2">Paiement sécurisé</h4>
                                    <p className="text-sm text-blue-600 mb-4">
                                        Vos informations sont protégées par le cryptage SSL 256-bit
                                    </p>
                                    <div className="flex justify-center space-x-4">
                                        <div className="text-xs text-gray-500">
                                            <i className="fas fa-lock mr-1"></i>
                                            PCI DSS
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            <i className="fas fa-check-circle mr-1"></i>
                                            3D Secure
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            <i className="fas fa-shield mr-1"></i>
                                            Fraud Protect
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Help & Support */}
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <h4 className="font-medium text-gray-700 mb-2">Besoin d'aide ?</h4>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li className="flex items-start">
                                        <i className="fas fa-phone text-gray-400 mr-2 mt-0.5"></i>
                                        <span>Support: 01 23 45 67 89</span>
                                    </li>
                                    <li className="flex items-start">
                                        <i className="fas fa-envelope text-gray-400 mr-2 mt-0.5"></i>
                                        <span>paiement@tale-platform.com</span>
                                    </li>
                                    <li className="flex items-start">
                                        <i className="fas fa-clock text-gray-400 mr-2 mt-0.5"></i>
                                        <span>Disponible 24h/24</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </TaleDashbaordMainCard>
        </div>
    );
};

// Payment Method Option Component
const PaymentMethodOption = ({ method, label, description, icon, color, isSelected, onSelect }) => {
    const colorClasses = {
        blue: { bg: 'bg-blue-100', text: 'text-blue-500', border: 'border-blue-500' },
        purple: { bg: 'bg-purple-100', text: 'text-purple-500', border: 'border-purple-500' },
        green: { bg: 'bg-green-100', text: 'text-green-500', border: 'border-green-500' }
    };

    const colorSet = colorClasses[color] || colorClasses.blue;

    return (
        <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                isSelected
                    ? `${colorSet.border} ${colorSet.bg} bg-opacity-20`
                    : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={onSelect}
        >
            <div className="flex items-center">
                <div className="mr-4">
                    <div className={`w-12 h-12 rounded-full ${colorSet.bg} flex items-center justify-center`}>
                        <i className={`fas fa-${icon} text-xl ${colorSet.text}`}></i>
                    </div>
                </div>
                <div className="flex-1">
                    <div className="font-semibold text-gray-800">{label}</div>
                    <div className="text-sm text-gray-600">{description}</div>
                </div>
                <div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? `${colorSet.border} ${colorSet.bg}` : 'border-gray-400'
                    }`}>
                        {isSelected && (
                            <div className={`w-3 h-3 rounded-full ${colorSet.border.replace('border-', 'bg-')}`}></div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
